import { useState, useEffect, useRef } from "react";
import { makeTr } from "@/i18n/tr";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { 
  Wallet, 
  ArrowUpCircle, 
  ArrowDownCircle,
  CreditCard,
  Clock,
  Loader2,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Smartphone,
  Gamepad2,
  Gift,
  Send,
  ChevronRight,
  Globe,
  Search
} from "lucide-react";
import { API_URL } from "@/config/api";

// Mobile operators data - mapped to real service IDs
const MOBILE_COUNTRIES = [
  {
    code: "RU",
    name: "Россия",
    flag: "🇷🇺",
    operators: [
      { id: "mts_ru", name: "МТС", logo: "📱" },
      { id: "beeline_ru", name: "Билайн", logo: "🐝" },
      { id: "tele2_ru", name: "Теле2", logo: "📞" },
      { id: "tinkoff_mobile", name: "Тинькофф Мобайл", logo: "💛" },
    ]
  },
  {
    code: "KZ",
    name: "Казахстан",
    flag: "🇰🇿",
    operators: [
      { id: "kcell", name: "Kcell", logo: "📱" },
      { id: "beeline_kz", name: "Beeline", logo: "🐝" },
      { id: "activ", name: "Activ", logo: "📶" },
      { id: "altel", name: "Altel", logo: "🌐" },
      { id: "tele2_kz", name: "Tele2", logo: "📞" },
    ]
  },
  {
    code: "KG",
    name: "Кыргызстан",
    flag: "🇰🇬",
    operators: [
      { id: "megacom", name: "MegaCom", logo: "📱" },
      { id: "beeline_kg", name: "Beeline", logo: "🐝" },
      { id: "o_kg", name: "О!", logo: "🔴" },
      { id: "o_mobile_kg", name: "О Мобайл", logo: "📶" },
    ]
  },
  {
    code: "UZ",
    name: "Узбекистан", 
    flag: "🇺🇿",
    operators: [
      { id: "beeline_uz", name: "Beeline", logo: "🐝" },
      { id: "ucell", name: "Ucell", logo: "📱" },
      { id: "ums", name: "UMS", logo: "📶" },
      { id: "mobiuz", name: "Mobiuz", logo: "🌐" },
      { id: "perfectum", name: "Perfectum Mobile", logo: "📞" },
    ]
  },
  {
    code: "AM",
    name: "Армения",
    flag: "🇦🇲",
    operators: [
      { id: "vivacell_mts", name: "VivaCell-MTS", logo: "📱" },
      { id: "beeline_am", name: "Beeline", logo: "🐝" },
      { id: "ucom_am", name: "Ucom (Orange)", logo: "🍊" },
    ]
  },
  {
    code: "AZ",
    name: "Азербайджан",
    flag: "🇦🇿",
    operators: [
      { id: "azercell", name: "Azercell", logo: "📱" },
    ]
  },
  {
    code: "TJ",
    name: "Таджикистан",
    flag: "🇹🇯",
    operators: [
      { id: "megafon_tj", name: "Мегафон", logo: "📶" },
      { id: "o_mobile_tj", name: "O-mobile", logo: "🔴" },
    ]
  },
];

// Gift cards data with real service IDs
const GIFT_CARDS = [
  {
    category: "playstation",
    name: "PlayStation",
    items: [
      // PlayStation Wallet USD
      { id: "ps_usd_10", name: "PS Store $10", logo: "🎮", price: 1000, currency: "USD", value: "10" },
      { id: "ps_usd_25", name: "PS Store $25", logo: "🎮", price: 2500, currency: "USD", value: "25" },
      { id: "ps_usd_50", name: "PS Store $50", logo: "🎮", price: 5000, currency: "USD", value: "50" },
      { id: "ps_usd_75", name: "PS Store $75", logo: "🎮", price: 7500, currency: "USD", value: "75" },
      { id: "ps_usd_100", name: "PS Store $100", logo: "🎮", price: 10000, currency: "USD", value: "100" },
    ]
  },
  {
    category: "playstation_try",
    name: "PlayStation (TRY)",
    items: [
      // PlayStation Store TRY (Turkish)
      { id: "ps_try_250", name: "PS Store 250₺", logo: "🎯", price: 800, currency: "TRY", value: "250" },
      { id: "ps_try_500", name: "PS Store 500₺", logo: "🎯", price: 1600, currency: "TRY", value: "500" },
      { id: "ps_try_750", name: "PS Store 750₺", logo: "🎯", price: 2400, currency: "TRY", value: "750" },
      { id: "ps_try_1000", name: "PS Store 1000₺", logo: "🎯", price: 3200, currency: "TRY", value: "1000" },
      { id: "ps_try_1500", name: "PS Store 1500₺", logo: "🎯", price: 4800, currency: "TRY", value: "1500" },
      { id: "ps_try_2000", name: "PS Store 2000₺", logo: "🎯", price: 6400, currency: "TRY", value: "2000" },
      { id: "ps_try_2500", name: "PS Store 2500₺", logo: "🎯", price: 8000, currency: "TRY", value: "2500" },
      { id: "ps_try_3000", name: "PS Store 3000₺", logo: "🎯", price: 9600, currency: "TRY", value: "3000" },
      { id: "ps_try_4000", name: "PS Store 4000₺", logo: "🎯", price: 12800, currency: "TRY", value: "4000" },
      { id: "ps_try_5000", name: "PS Store 5000₺", logo: "🎯", price: 16000, currency: "TRY", value: "5000" },
    ]
  },
  {
    category: "other",
    name: "Другие (скоро) / Others (soon)",
    items: [
      { id: "steam", name: "Steam", logo: "🎮", price: 0, currency: "USD", value: "Soon", disabled: true },
      { id: "xbox", name: "Xbox", logo: "🟢", price: 0, currency: "USD", value: "Soon", disabled: true },
      { id: "nintendo", name: "Nintendo", logo: "🍄", price: 0, currency: "USD", value: "Soon", disabled: true },
    ]
  },
];

// Quick amounts in Points (1 Point = 1 RUB)
const QUICK_AMOUNTS_POINTS = [100, 300, 500, 1000, 3000, 5000];

// Currency rates will be fetched from API
const DEFAULT_USD_RATE = 1.0;

export default function WalletPage() {
  const { t, i18n } = useTranslation();
  const { user, getBalance, getTransactions, deposit, withdraw, fetchUser } = useStore();
  const token = useStore((s) => s.token) || localStorage.getItem("token");
  
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Currency rates state
  const [currencyRates, setCurrencyRates] = useState({ RUB: DEFAULT_USD_RATE });
  const [selectedCurrency, setSelectedCurrency] = useState("RUB");
  
  // Deposit/Withdraw state
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  
  // Mobile top-up state
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [topupAmount, setTopupAmount] = useState("");
  
  // Gift cards state
  const [giftDialogOpen, setGiftDialogOpen] = useState(false);
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);
  const [selectedGiftAmount, setSelectedGiftAmount] = useState(null);
  
  // Transfer state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(null);
  const pendingTimerRef = useRef(null);

  const isRu = i18n.language === "ru";
  const tr = makeTr(i18n.language);
  
  // Calculate points from currency amount
  const convertToPoints = (amount, currency = selectedCurrency) => {
    const rate = currencyRates[currency] || DEFAULT_USD_RATE;
    return (amount / rate).toFixed(2);
  };
  
  const rubToPoints = (rub) => parseFloat(convertToPoints(rub, "RUB"));
  const fmtPoints = (rub) => `${rubToPoints(rub).toFixed(2)} P`;
  const cardPoints = (card) => parseFloat(convertToPoints(parseFloat(card.value), card.currency));
  const fmtCardPoints = (card) => `${cardPoints(card).toFixed(2)} P`;

  // Calculate currency from points
  const convertFromPoints = (points, currency = selectedCurrency) => {
    const rate = currencyRates[currency] || DEFAULT_USD_RATE;
    return (points * rate).toFixed(2);
  };

  useEffect(() => {
    loadData();
    loadCurrencyRates();
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const savedTx = localStorage.getItem("pending_deposit");
    if (status === "fail") {
      setTimeout(() => toast.error(tr("Платёж не прошёл", "Payment failed")), 400);
      localStorage.removeItem("pending_deposit");
    } else if (savedTx) {
      try {
        const saved = JSON.parse(savedTx);
        if (Date.now() - saved.startedAt < 30 * 60 * 1000) setPendingDeposit(saved);
        else localStorage.removeItem("pending_deposit");
      } catch {
        localStorage.removeItem("pending_deposit");
      }
    }
    if (status) window.history.replaceState({}, "", window.location.pathname);
    return () => clearInterval(pendingTimerRef.current);
  }, []);

  // Poll the pending deposit until the gateway confirms it
  useEffect(() => {
    clearInterval(pendingTimerRef.current);
    if (!pendingDeposit) return undefined;
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/wallet/transactions/${pendingDeposit.txId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.status === 404 || res.status === 401) {
          clearInterval(pendingTimerRef.current);
          localStorage.removeItem("pending_deposit");
          setPendingDeposit(null);
          return;
        }
        if (!res.ok) return;
        const tx = await res.json();
        if (tx.status === "completed") {
          clearInterval(pendingTimerRef.current);
          localStorage.removeItem("pending_deposit");
          setPendingDeposit(null);
          toast.success(t("wallet.depositSuccess"), { description: `+${Number(tx.amount).toFixed(2)} Points` });
          await loadData();
          await fetchUser();
        } else if (tx.status === "failed") {
          clearInterval(pendingTimerRef.current);
          localStorage.removeItem("pending_deposit");
          setPendingDeposit(null);
          toast.error(tr("Платёж не прошёл", "Payment failed"));
          await loadData();
        }
      } catch (e) {
        console.error(e);
      }
    };
    check();
    pendingTimerRef.current = setInterval(check, 4000);
    return () => clearInterval(pendingTimerRef.current);
  }, [pendingDeposit]);

  const cancelPendingDeposit = () => {
    clearInterval(pendingTimerRef.current);
    localStorage.removeItem("pending_deposit");
    setPendingDeposit(null);
    loadData();
  };

  const loadCurrencyRates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/currency/rates`);
      if (response.ok) {
        const data = await response.json();
        setCurrencyRates(data.rates || { RUB: DEFAULT_USD_RATE });
      }
    } catch (error) {
      console.error("Failed to load currency rates:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    const [bal, txs] = await Promise.all([
      getBalance(),
      getTransactions()
    ]);
    setBalance(bal);
    setTransactions(txs);
    setLoading(false);
  };

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      toast.error(tr("Неверная сумма", "Invalid amount"));
      return;
    }
    if (amount < 50) {
      toast.error(tr("Минимальная сумма 50 ₽", "Minimum amount is 50 ₽"));
      return;
    }

    setProcessing(true);
    // Open the tab synchronously inside the click so popup blockers allow it
    const payWindow = window.open("", "_blank");
    if (payWindow) {
      try {
        payWindow.document.write('<p style="font-family:sans-serif;padding:24px;color:#444">Переход на страницу оплаты… / Redirecting to payment…</p>');
      } catch (e) { /* ignore */ }
    }
    const result = await deposit(amount, paymentMethod);
    
    if (result.success) {
      if (result.payment_url) {
        const pending = {
          txId: result.transaction_id,
          amount,
          points: result.points,
          method: paymentMethod,
          url: result.payment_url,
          startedAt: Date.now()
        };
        localStorage.setItem("pending_deposit", JSON.stringify(pending));
        setProcessing(false);
        setDepositDialogOpen(false);
        setDepositAmount("");
        setPendingDeposit(pending);
        if (payWindow) {
          payWindow.opener = null;
          payWindow.location.href = result.payment_url;
        } else {
          toast.info(tr("Нажмите «Открыть страницу оплаты»", "Click “Open payment page”"));
        }
        return;
      } else {
        if (payWindow) payWindow.close();
        toast.success(t("wallet.depositSuccess"), { description: `+${amount} ₽ ≈ ${fmtPoints(amount)}` });
      }
      setDepositAmount("");
      setDepositDialogOpen(false);
      await loadData();
      await fetchUser();
    } else {
      if (payWindow) payWindow.close();
      toast.error(result.error || (tr("Ошибка пополнения", "Deposit failed")));
    }
    setProcessing(false);
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error(tr("Неверная сумма", "Invalid amount"));
      return;
    }
    if (amount > balance) {
      toast.error(t("wallet.insufficientBalance"));
      return;
    }
    if (!cardNumber || cardNumber.length < 16) {
      toast.error(tr("Неверный номер карты", "Invalid card number"));
      return;
    }
    if (user?.kyc_status !== "approved") {
      toast.error(t("profile.kyc.required"));
      return;
    }

    setProcessing(true);
    const result = await withdraw(amount, cardNumber);
    
    if (result.success) {
      toast.success(t("wallet.withdrawSuccess"), { description: `-${amount} Points` });
      setWithdrawAmount("");
      setCardNumber("");
      setWithdrawDialogOpen(false);
      await loadData();
      await fetchUser();
    } else {
      toast.error(result.error || (tr("Ошибка вывода", "Withdrawal failed")));
    }
    setProcessing(false);
  };

  const handleMobileTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!selectedOperator || !phoneNumber || !amount) {
      toast.error(tr("Заполните все поля", "Fill all fields"));
      return;
    }
    if (rubToPoints(amount) > balance) {
      toast.error(t("wallet.insufficientBalance"));
      return;
    }
    if (phoneNumber.length < 10) {
      toast.error(tr("Неверный номер телефона", "Invalid phone number"));
      return;
    }

    setProcessing(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/services/mobile-topup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          operator_id: selectedOperator.id,
          phone_number: phoneNumber,
          amount: amount
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(tr("Телефон пополнен!", "Phone topped up!"), {
          description: `${selectedOperator.name}: +${amount} ₽ (−${fmtPoints(amount)})`
        });
        setMobileDialogOpen(false);
        resetMobileForm();
        await loadData();
        await fetchUser();
      } else {
        toast.error(data.detail || (tr("Ошибка пополнения", "Top-up failed")));
      }
    } catch (error) {
      toast.error(tr("Ошибка сети", "Network error"));
    }
    
    setProcessing(false);
  };

  const handleGiftCardPurchase = async () => {
    if (!selectedGiftCard) {
      toast.error(tr("Выберите карту", "Select card"));
      return;
    }
    
    if (selectedGiftCard.disabled) {
      toast.info(tr("Скоро будет доступно!", "Coming soon!"));
      return;
    }
    
    if (cardPoints(selectedGiftCard) > balance) {
      toast.error(t("wallet.insufficientBalance"));
      return;
    }

    setProcessing(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/services/gift-card`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          card_id: selectedGiftCard.id,
          amount: selectedGiftCard.price
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(tr("Карта куплена!", "Card purchased!"), {
          description: data.gift_code ? `Код: ${data.gift_code}` : selectedGiftCard.name
        });
        setGiftDialogOpen(false);
        setSelectedGiftCard(null);
        await loadData();
        await fetchUser();
      } else {
        toast.error(data.detail || (tr("Ошибка покупки", "Purchase failed")));
      }
    } catch (error) {
      toast.error(tr("Ошибка сети", "Network error"));
    }
    
    setProcessing(false);
  };

  const resetMobileForm = () => {
    setSelectedCountry(null);
    setSelectedOperator(null);
    setPhoneNumber("");
    setTopupAmount("");
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case "deposit": return <ArrowDownCircle className="h-5 w-5 text-green-500" />;
      case "withdrawal": return <ArrowUpCircle className="h-5 w-5 text-red-500" />;
      case "mobile_topup": return <Smartphone className="h-5 w-5 text-blue-500" />;
      case "gift_card": return <Gift className="h-5 w-5 text-purple-500" />;
      case "stake": return <TrendingDown className="h-5 w-5 text-yellow-500" />;
      case "win": return <TrendingUp className="h-5 w-5 text-green-500" />;
      default: return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-6 pb-20" data-testid="wallet-page">
      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">
              {tr("Баланс (Points)", "Balance (Points)")}
            </p>
            <p className="text-4xl font-black text-primary">
              {loading ? "..." : `${balance.toFixed(2)} P`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              1 Point = 1 ₽
            </p>
          </div>
          <div className="flex gap-3 mt-6">
            <Button 
              onClick={() => setDepositDialogOpen(true)}
              data-testid="open-deposit-btn"
              className="flex-1 btn-gold"
            >
              <ArrowDownCircle className="h-4 w-4 mr-2" />
              {tr("Пополнить", "Deposit")}
            </Button>
            <Button 
              onClick={() => setWithdrawDialogOpen(true)}
              variant="outline"
              className="flex-1"
            >
              <ArrowUpCircle className="h-4 w-4 mr-2" />
              {tr("Вывести", "Withdraw")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Services Grid */}
      <div>
        <h2 className="text-lg font-bold mb-4">{tr("Услуги", "Services")}</h2>
        <div className="grid grid-cols-2 gap-3">
          {/* Mobile Top-up */}
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setMobileDialogOpen(true)}
          >
            <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center mb-2">
                <Smartphone className="h-6 w-6 text-blue-500" />
              </div>
              <p className="font-semibold text-sm">{tr("Мобильная связь", "Mobile")}</p>
              <p className="text-xs text-muted-foreground">{tr("Пополнение телефона", "Phone top-up")}</p>
            </CardContent>
          </Card>

          {/* Gift Cards */}
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setGiftDialogOpen(true)}
          >
            <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center mb-2">
                <Gamepad2 className="h-6 w-6 text-purple-500" />
              </div>
              <p className="font-semibold text-sm">{tr("Gift-карты", "Gift Cards")}</p>
              <p className="text-xs text-muted-foreground">Steam, PS, Xbox</p>
            </CardContent>
          </Card>

          {/* Card Transfer */}
          <Card 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setTransferDialogOpen(true)}
          >
            <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center mb-2">
                <CreditCard className="h-6 w-6 text-green-500" />
              </div>
              <p className="font-semibold text-sm">{tr("На карту", "To Card")}</p>
              <p className="text-xs text-muted-foreground">Visa, MC, МИР</p>
            </CardContent>
          </Card>

          {/* More Services */}
          <Card className="opacity-50">
            <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-2">
                <Globe className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-semibold text-sm">{tr("Ещё", "More")}</p>
              <p className="text-xs text-muted-foreground">{tr("Скоро", "Soon")}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Transaction History */}
      <div>
        <h2 className="text-lg font-bold mb-4">{tr("История", "History")}</h2>
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {tr("Нет транзакций", "No transactions")}
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="divide-y divide-border">
                  {transactions.map((tx, i) => (
                    <div key={i} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {getTransactionIcon(tx.type)}
                        <div>
                          <p className="font-medium text-sm capitalize">
                            {tx.type === "mobile_topup" ? (tr("Моб. связь", "Mobile")) : tx.type}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${tx.status === "pending" ? "text-muted-foreground" : tx.status === "failed" ? "text-red-400 line-through" : tx.amount > 0 ? "text-green-500" : "text-red-500"}`} data-testid="tx-amount">
                          {tx.amount > 0 ? "+" : ""}{Number(tx.amount).toFixed(2)} P
                        </p>
                        {tx.status && tx.status !== "completed" && (
                          <span className={`text-[10px] uppercase tracking-wide ${tx.status === "failed" ? "text-red-400" : "text-amber-400"}`} data-testid="tx-status">
                            {tx.status === "pending" ? (tr("ожидает оплаты", "pending")) : tx.status === "failed" ? (tr("не прошёл", "failed")) : tx.status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deposit Dialog */}
      {/* Waiting for payment */}
      <Dialog open={!!pendingDeposit} onOpenChange={(open) => { if (!open) cancelPendingDeposit(); }}>
        <DialogContent className="max-w-sm" data-testid="pending-deposit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {tr("Ожидаем оплату", "Waiting for payment")}
            </DialogTitle>
            <DialogDescription>
              {pendingDeposit?.method === "sbp"
                ? tr("Отсканируйте QR-код в приложении банка и подтвердите платёж. Баланс обновится автоматически — закрывать страницу не нужно.",
                     "Scan the QR code in your bank app and confirm the payment. Your balance updates automatically — keep this page open.")
                : tr("Завершите оплату на странице банка. Баланс обновится автоматически.",
                     "Complete the payment on the bank page. Your balance updates automatically.")}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-muted/50 p-3 text-sm flex justify-between" data-testid="pending-deposit-amount">
            <span>{pendingDeposit?.amount} ₽</span>
            <span className="font-semibold text-primary">≈ {Number(pendingDeposit?.points || 0).toFixed(2)} P</span>
          </div>
          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={() => window.open(pendingDeposit?.url, "_blank", "noopener")} data-testid="reopen-payment-btn">
              {tr("Открыть страницу оплаты", "Open payment page")}
            </Button>
            <Button variant="ghost" className="text-muted-foreground" onClick={cancelPendingDeposit} data-testid="cancel-pending-btn">
              {tr("Закрыть", "Close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{tr("Пополнить баланс", "Deposit")}</DialogTitle>
            <DialogDescription>{tr("Выберите сумму и способ оплаты", "Choose amount and payment method")} · 1 Point = 1 ₽{depositAmount ? ` · ${tr("получите", "you get")} ${parseFloat(depositAmount) || 0} P` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {[100, 500, 1000, 5000].map(amt => (
                <Button
                  key={amt}
                  variant={depositAmount === String(amt) ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDepositAmount(String(amt))}
                >
                  {amt}₽
                </Button>
              ))}
            </div>
            <Input
              type="number"
              placeholder={tr("Или введите сумму", "Or enter amount")}
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              min={50}
            />
            <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
              <div className="flex items-center space-x-2 p-3 rounded-lg border">
                <RadioGroupItem value="card" id="card" />
                <Label htmlFor="card" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    {tr("Банковская карта", "Bank Card")}
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg border">
                <RadioGroupItem value="sbp" id="sbp" />
                <Label htmlFor="sbp" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    СБП
                  </div>
                </Label>
              </div>
            </RadioGroup>
            <Button onClick={handleDeposit} className="w-full btn-gold" disabled={processing} data-testid="deposit-submit-btn">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : (tr("Пополнить", "Deposit"))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{tr("Вывести средства", "Withdraw")}</DialogTitle>
            <DialogDescription>{tr("На банковскую карту", "To bank card")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{tr("Сумма", "Amount")}</Label>
              <Input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="0"
                max={balance}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {tr("Доступно", "Available")}: {balance} Points
              </p>
            </div>
            <div>
              <Label>{tr("Номер карты", "Card Number")}</Label>
              <Input
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, "").slice(0, 16))}
                placeholder="0000 0000 0000 0000"
              />
            </div>
            <Button onClick={handleWithdraw} className="w-full" disabled={processing} data-testid="withdraw-submit-btn">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : (tr("Вывести", "Withdraw"))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Top-up Dialog */}
      <Dialog open={mobileDialogOpen} onOpenChange={(open) => { setMobileDialogOpen(open); if (!open) resetMobileForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-blue-500" />
              {tr("Пополнение телефона", "Mobile Top-up")}
            </DialogTitle>
          </DialogHeader>
          
          {!selectedCountry ? (
            // Step 1: Select Country
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-3">{tr("Выберите страну", "Select country")}</p>
              {MOBILE_COUNTRIES.map(country => (
                <div
                  key={country.code}
                  onClick={() => setSelectedCountry(country)}
                  className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{country.flag}</span>
                    <span className="font-medium">{country.name}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          ) : !selectedOperator ? (
            // Step 2: Select Operator
            <div className="space-y-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedCountry(null)} className="mb-2">
                ← {selectedCountry.flag} {selectedCountry.name}
              </Button>
              <p className="text-sm text-muted-foreground mb-3">{tr("Выберите оператора", "Select operator")}</p>
              {selectedCountry.operators.map(op => (
                <div
                  key={op.id}
                  onClick={() => setSelectedOperator(op)}
                  className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{op.logo}</span>
                    <span className="font-medium">{op.name}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          ) : (
            // Step 3: Enter Details
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => setSelectedOperator(null)} className="mb-2">
                ← {selectedOperator.logo} {selectedOperator.name}
              </Button>
              
              <div>
                <Label>{tr("Номер телефона", "Phone Number")}</Label>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                  placeholder="+7 XXX XXX XX XX"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>{tr("Сумма", "Amount")}</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {QUICK_AMOUNTS_POINTS.map(amt => (
                    <Button
                      key={amt}
                      variant={topupAmount === String(amt) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTopupAmount(String(amt))}
                    >
                      {amt}₽
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  placeholder={tr("Другая сумма", "Other amount")}
                  className="mt-2"
                  min={10}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {tr("Баланс", "Balance")}: {balance} Points{topupAmount ? ` · ${tr("спишется", "cost")} ${fmtPoints(parseFloat(topupAmount) || 0)}` : ""}
                </p>
              </div>

              <Button 
                onClick={handleMobileTopup} 
                className="w-full btn-gold" 
                disabled={processing || !phoneNumber || !topupAmount}
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {tr("Пополнить", "Top Up")} {topupAmount ? `${topupAmount} ₽` : ""}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gift Cards Dialog */}
      <Dialog open={giftDialogOpen} onOpenChange={(open) => { setGiftDialogOpen(open); if (!open) setSelectedGiftCard(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-purple-500" />
              Gift-{tr("карты", "Cards")}
            </DialogTitle>
          </DialogHeader>
          
          {!selectedGiftCard ? (
            <ScrollArea className="h-[400px] pr-4">
              {GIFT_CARDS.map(category => (
                <div key={category.category} className="mb-6">
                  <h3 className="font-semibold mb-3 text-sm text-muted-foreground">{category.name}</h3>
                  <div className="space-y-2">
                    {category.items.map(item => (
                      <div
                        key={item.id}
                        onClick={() => !item.disabled && setSelectedGiftCard(item)}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          item.disabled 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:border-primary/50 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{item.logo}</span>
                          <div>
                            <span className="font-medium text-sm">{item.name}</span>
                            {!item.disabled && (
                              <p className="text-xs text-muted-foreground">
                                {item.value} {item.currency}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {item.disabled ? (
                            <span className="text-xs text-muted-foreground">{tr("Скоро", "Soon")}</span>
                          ) : (
                            <>
                              <p className="font-bold text-primary">{fmtCardPoints(item)}</p>
                              <ChevronRight className="h-4 w-4 text-muted-foreground inline" />
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </ScrollArea>
          ) : (
            <div className="space-y-4">
              <Button variant="ghost" size="sm" onClick={() => setSelectedGiftCard(null)}>
                ← {tr("Назад", "Back")}
              </Button>
              
              <div className="text-center py-4">
                <span className="text-4xl mb-2 block">{selectedGiftCard.logo}</span>
                <h3 className="font-bold text-lg">{selectedGiftCard.name}</h3>
                <p className="text-2xl font-bold text-primary mt-2">{fmtCardPoints(selectedGiftCard)}</p>
                <p className="text-sm text-muted-foreground">
                  {tr("Номинал", "Value")}: {selectedGiftCard.value} {selectedGiftCard.currency}
                </p>
              </div>

              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  {tr("Баланс", "Balance")}: <span className="font-bold">{balance} Points</span>
                </p>
                {cardPoints(selectedGiftCard) > balance && (
                  <p className="text-xs text-red-500 mt-1">
                    {tr("Недостаточно средств", "Insufficient balance")}
                  </p>
                )}
              </div>

              <Button 
                onClick={handleGiftCardPurchase} 
                className="w-full btn-gold" 
                disabled={processing || cardPoints(selectedGiftCard) > balance}
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Gift className="h-4 w-4 mr-2" />
                    {tr("Купить за", "Buy for")} {fmtCardPoints(selectedGiftCard)}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Card Transfer Dialog (Stub) */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-500" />
              {tr("Перевод на карту", "Card Transfer")}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-2">{tr("Скоро!", "Coming Soon!")}</h3>
            <p className="text-sm text-muted-foreground">
              {isRu 
                ? "Переводы на карту появятся в ближайшее время" 
                : "Card transfers will be available soon"}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
